'use client';

import { makeRatingIconsData } from '@/directory/makeRatingIconsData';
import { theme } from '@/theme';

import { Currency } from '@/components/atoms/Currency/Currency';

interface DistanceProps {
  value?: number;
}
const Distance = ({ value }: DistanceProps) =>
  value ? <>{value.toFixed(1)}</> : null;

interface SummaryItemProps {
  summaryItem: [key: string, value: string];
  careType: string;
}
export const buildSummaryItem = ({
  summaryItem: [key, value],
  careType,
}: SummaryItemProps) => {
  const isCareHome = careType === 'careHome';
  const isHomeCare = careType === 'homeCare';

  const ratingIconsData = makeRatingIconsData(theme);

  const summaryMap: { [key: string]: (val: any) => any } = {
    rating: (rating?: string) => {
      if (!rating) return undefined;

      const iconData = ratingIconsData[rating] || ratingIconsData.notPublished;

      return {
        label: iconData.label,
        icon: iconData.icon,
        iconType: iconData.iconType,
        color: iconData.color,
        description: "Regulator's rating",
        show: !!rating,
      };
    },
    pricePerWeek: (price?: number) => ({
      label: price && <Currency value={price} />,
      description: 'Weekly starting price',
      icon: 'pound-sign',
      show: !!price && isCareHome,
    }),
    bedCount: (beds?: number) => ({
      label: `${beds} beds`,
      description: 'Maximum occupancy',
      icon: 'bed',
      show: !!beds,
    }),
    dateOpened: (date?: any) => ({
      label: date && new Date(date).getFullYear(),
      description: 'Date opened',
      icon: 'hotel',
      show: !!date,
    }),
    availability: (available?: boolean) => ({
      label: { careHome: 'Vacancies', homeCare: 'Accepting clients' }[careType],
      description: {
        careHome: available ? 'Current availability' : 'No rooms this week',
      }[careType],
      icon: available ? 'check' : 'close',
      color: available ? '#51ac7c' : '#bdbdbd',
      show: isCareHome || !!available,
    }),
    hourlyRate: (rate?: number) => ({
      label: rate && <Currency value={rate} />,
      description: 'Starting hourly rate',
      icon: 'pound-sign',
      show: !!rate && isHomeCare,
    }),
    managerName: (name?: string) => ({
      label: name,
      description: 'Agency manager',
      icon: 'user',
      show: !!name && isHomeCare,
    }),
    minimumVisitTime: (label?: string) => ({
      label,
      description: 'Minimum visit time',
      icon: 'clock',
      show: !!label && isHomeCare,
    }),
    distance: (value?: number) => ({
      show: !!value && isCareHome,
      key: 'distance',
      icon: 'map-marker',
      iconType: 'fas',
      color: '#000000',
      label: (
        <>
          <Distance value={value} /> miles away
        </>
      ),
    }),
  };

  return summaryMap[key]?.(value);
};
